import assign from 'lodash/assign';
import chunk from 'lodash/chunk';
import forEach from 'lodash/forEach';
import isUndefined from 'lodash/isUndefined';
import uniq from 'lodash/uniq';
import get from 'lodash/get';
import filter from 'lodash/filter';
import map from 'lodash/map';
import negate from 'lodash/negate';
import omitBy from 'lodash/omitBy';
import without from 'lodash/without';
import request from '@flowio/request';
import session from '@flowio/session';

import config from '../config';
import cache from '../cache';
import constants from '../utilities/tags/constants';
import getAttributes from '../utilities/tags/get-attributes';
import localizeElement from '../utilities/tags/localize-element';
import revealNestedTags from '../utilities/tags/reveal-nested-tags';
import optionsSuccess from '../utilities/options-success';
import optionsError from '../utilities/options-error';
import raven from '../utilities/raven';
import hasClass from '../utilities/dom/has-class';
import { isApiShopifyLocalizationMethod } from '../utilities/shopify-localization-method';
import { memo } from '../utilities/memo';

class Variants {
  constructor() {
    this.doesNotHaveClass = negate(hasClass);
    this.organizationId = config.FLOW_ORGANIZATION;
    this.getLocalCurrency = () => get(session.getSession(), 'geo.currency.iso_4217_3');
    this.getExperienceCurrency = () => get(session.getSession(), 'experience.currency');
    this.getExperienceRegion = () => get(session.getSession(), 'experience.region.id');

    this.fetchLocalizedVariants = memo((experienceKey, params) => new Promise((resolve, reject) => {
      const baseUrl = config.API_BASE_URI;
      const url = `${baseUrl}/${this.organizationId}/shopify/localized/variants/experience/${experienceKey}/map`;

      request.anonymous({
        url,
        data: params,
      }, (status, response) => {
        if (status === 200) {
          resolve(response);
        } else {
          reject(response);
        }
      });
    }), [this.organizationId]);

    this.addLocalizedVariantsToCache = (variants, options) => {
      forEach(variants, (variant) => {
        cache.getCache().localizedVariants.addVariant(variant, options);
      });
    };

    this.localizeElements = (options = {}) => {
      try {
        const container = options.container || window.document;
        const force = get(options, 'force', false);
        const variantElements = container.querySelectorAll('[flow-variant]');

        // Localize each element individually
        forEach(variantElements, (variantElement) => {
          const attributes = getAttributes(variantElement);
          const data = cache.getCache().localizedVariants.getById(attributes.id);
          if (isApiShopifyLocalizationMethod() ||
            force ||
            !hasClass(variantElement, constants.FLOW_LOCALIZED_CLASS)) {
            localizeElement(variantElement, attributes, data);
          }
        });

        revealNestedTags();

        optionsSuccess(options);
      } catch (error) {
        raven.captureError(error);
        optionsError(options, error);
      }
    };
  }

  /**
   * Get localized data for variants.
   *
   * This will return a map of variantId -> LocalizedVariant.
   *
   * @param {Object}    options
   * @param {array}     options.variants     Optional. List of variant ids for which
   *                                         to get localized data
   * @param {string}    options.experience   Optional. Filter results by experience
   * @param {string}    options.currency   Optional. Return results in a specific currency
   * @param {function}  options.success      Optional. success(status, variants)
   * @param {function}  options.error        Optional. error(status, error)
   */
  getMapByExperience(options = {}) {
    const experienceKey = session.getExperience();
    const country = session.getCountry();

    if (experienceKey === undefined) {
      optionsSuccess(options, 204, []);
      return;
    }

    const limit = get(options, 'limit', 100);
    const variants = uniq(get(options, 'variants', []));
    const promises = map(chunk(variants, limit), chunks => (
      this.fetchLocalizedVariants(experienceKey, omitBy({
        country,
        limit,
        handle: get(options, 'handles'),
        currency: get(options, 'currency') || this.getLocalCurrency(),
        variant_id: chunks,
      }, isUndefined))
    ));

    // Aggregate the chunk responses into a single map of variant ids
    Promise.all(promises)
      .then(responses => optionsSuccess(options, 200, assign({}, ...responses)))
      .catch(response => optionsError(options, response));
  }

  load(options) {
    const opts = assign({}, options, {
      success: function successOverride(status, variants) {
        if (status === 200) {
          this.addLocalizedVariantsToCache(variants, options);
          optionsSuccess(options, status, variants);
        } else {
          optionsError(options, variants);
        }
      }.bind(this),
    });

    this.getMapByExperience(opts);
  }

  /**
   * A function responsible for fetching localized variants matching the specified handles.
   * @param {String[]} [options.handles = []]
   * @param {Number} [options.limit = 100]
   * @param {String} [options.currency]
   * @param {Function} [options.success]
   * @param {Function} [options.error]
   */
  loadByHandles(options) {
    const experienceKey = session.getExperience();
    const country = session.getCountry();

    if (experienceKey === undefined) {
      optionsSuccess(options, 204, []);
      return;
    }

    const limit = get(options, 'limit', 100);
    const handles = get(options, 'handles', []);
    const promises = map(chunk(handles, limit), chunks => (
      this.fetchLocalizedVariants(experienceKey, omitBy({
        limit,
        country,
        handle: chunks,
        currency: get(options, 'currency') || this.getLocalCurrency(),
      }, isUndefined))
    ));

    // Aggregate the chunk responses into a single map of variant ids
    Promise.all(promises)
      .then((responses) => {
        const variants = assign({}, ...responses);
        this.addLocalizedVariantsToCache(variants);
        optionsSuccess(options, 200, variants);
      })
      .catch((response) => {
        optionsError(options, response);
      });
  }

  /**
   * Searches for references to metafield data that hasn't been loaded on the page server side.
   * Attempts to fetch missing data from the API.
   */
  syncVariants(options = {}) {
    const container = options.container || window.document;
    const forceReload = get(options, 'forceReload', false);
    const cachedVariantIds = uniq(map(get(window.Flow, 'cache.localizedVariants.store'), v => v.id));
    const variantElements = container.querySelectorAll('[flow-variant]');
    const allElementIds = map(variantElements, element => parseInt(element.getAttribute('flow-variant'), 10));
    const notLocalizedElements = filter(variantElements, element => this.doesNotHaveClass(element, 'flow-localized'));
    const elementVariantIds = filter(
      map(notLocalizedElements, element => parseInt(element.getAttribute('flow-variant'), 10)),
    );
    const variantsToLoad = uniq(without(elementVariantIds, ...cachedVariantIds));
    this.load({
      overwrite: true,
      variants: forceReload ? allElementIds : variantsToLoad,
      currency: this.getLocalCurrency(),
      success: options.success,
      error: options.error,
    });
  }

  /**
   * Used to get pricing for a specific currency. This is necessary when the user
   * is located in a country that differs from the country & currency of the
   * assigned experience.
   */
  fetchMetafieldsByCountry(options = {}) {
    const variantIds = uniq(map(get(window.Flow, 'cache.localizedVariants.store'), v => v.id));

    if (variantIds.length === 0) {
      optionsSuccess(options, 204, []);
    } else {
      this.load({
        currency: this.getLocalCurrency(),
        overwrite: true,
        variants: variantIds,
        success: options.success,
        error: options.error,
      });
    }
  }

  localizeVariants(options = {}) {
    if ((this.getExperienceRegion() === 'world' ||
      this.getExperienceRegion() === 'eurozone' ||
      this.getExperienceRegion() === 'europeanunion') &&
      this.getLocalCurrency() !== this.getExperienceCurrency()) {
      this.fetchMetafieldsByCountry({
        success: () => {
          this.localizeElements(options);
        },
        error: options.error,
      });
    } else {
      this.localizeElements(options);
    }
  }

  localize(options = {}) {
    const force = get(options, 'force', false);

    if (force) {
      this.syncVariants({
        container: options.container,
        forceReload: true,
        success: () => {
          this.localizeVariants(options);
        },
        error: error => optionsError(options, error),
      });
    } else {
      this.localizeVariants(options);
    }
  }
}

export default Variants;



// WEBPACK FOOTER //
// ./static/js/v0/variants/variants.js